import React, { useRef, useState, useEffect } from "react";
import { Button, Message, Segment, Icon } from "semantic-ui-react";
import agent from "../../agent";
import { ALLOWED_EXTENSIONS, ACCEPT_STRING, MAX_FILE_SIZE, MAX_FILE_COUNT, MAX_FILENAME_LENGTH } from "../../consts/fileConstants";
import { hasRole, UserRole } from "../../helpers/UserRole";

interface EventFile {
    id: string;
    filename: string;
}

interface Props {
    fileIds: string[];
    onFilesChange: (fileIds: string[]) => void;
    filesToUpload: File[];
    onFilesToUploadChange: (files: File[]) => void;
    uploading?: boolean;
    readOnly?: boolean;
}

const EventFilesUpload = (props: Props) => {
    const [filesInfo, setFilesInfo] = useState<EventFile[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isObserver = hasRole(UserRole.OBSERVER);

    const onDownloadFile = async (id: string, filename: string) => {
        if (isObserver) return;
        try {
            const data: any = await agent.FileProvider.getFileById(id);
            if (data && data.body) {
                const blob = new Blob([data.body], { type: "application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }
        } catch (e) {
            console.error("Ошибка при скачивании файла:", e);
        }
    };

    const onDownloadLocalFile = (file: File) => {
        if (isObserver) return;
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    useEffect(() => {
        let isCancelled = false;
        const fetchFilesInfo = async () => {
            if (!props.fileIds || props.fileIds.length === 0) {
                setFilesInfo([]);
                return;
            }
            try {
                const infoPromises = props.fileIds.map(async (id) => {
                    const res = await agent.FileProvider.open(id);
                    return { id, filename: res.filename };
                });
                const resolvedInfos = await Promise.all(infoPromises);
                if (!isCancelled) {
                    setFilesInfo(resolvedInfos);
                }
            } catch (error) {
                console.error("Ошибка при получении файлов:", error);
            }
        };
        fetchFilesInfo();
        return () => {
            isCancelled = true;
        };
    }, [props.fileIds]);

    function truncateFilename(name: string): string {
        const lastDot = name.lastIndexOf('.');
        if (lastDot === -1) {
            return name.length > MAX_FILENAME_LENGTH ? name.substring(0, MAX_FILENAME_LENGTH) : name;
        }
        const baseName = name.substring(0, lastDot);
        const ext = name.substring(lastDot);
        if (baseName.length <= MAX_FILENAME_LENGTH) {
            return name;
        }
        const truncatedBase = baseName.substring(0, MAX_FILENAME_LENGTH);
        return truncatedBase + ext;
    }

    function validateFiles(selectedFiles: File[]): { valid: File[], errors: string[] } {
        const result: { valid: File[], errors: string[] } = { valid: [], errors: [] };
        
        const currentCount = (props.fileIds ? props.fileIds.length : 0) + props.filesToUpload.length;

        if (currentCount + selectedFiles.length > MAX_FILE_COUNT) {
            result.errors.push(`Максимальное количество файлов: ${MAX_FILE_COUNT} шт. Вы можете добавить еще ${Math.max(0, MAX_FILE_COUNT - currentCount)} файлов.`);
            return result;
        }

        for (const file of selectedFiles) {
            const extension = file.name.split('.').pop()?.toLowerCase();
            if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
                result.errors.push(`Файл «${file.name}» имеет недопустимый формат. Разрешённые форматы: ${ALLOWED_EXTENSIONS.join(', ')}`);
                continue;
            }
            if (file.size === 0) {
                result.errors.push(`Файл «${file.name}» пуст (размер 0 байт) и не может быть загружен.`);
                continue;
            }
            if (file.size > MAX_FILE_SIZE) {
                result.errors.push(`Файл «${file.name}» превышает максимальный размер ${MAX_FILE_SIZE / (1024 * 1024)} МБ`);
                continue;
            }
            result.valid.push(file);
        }

        return result;
    }

    function onFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
        setErrorMessage(null);
        let selectedFiles: File[] = [];
        if (event.target.files) {
            for (let i = 0; i < event.target.files.length; i++) {
                selectedFiles.push(event.target.files[i]);
            }
        }
        
        const { valid, errors } = validateFiles(selectedFiles);
        if (errors.length > 0) {
            setErrorMessage(errors.join('\n'));
        }

        const truncatedFiles = valid.map(f => {
            const newName = truncateFilename(f.name);
            if (newName !== f.name) {
                return new File([f], newName, { type: f.type });
            }
            return f;
        });

        props.onFilesToUploadChange([...props.filesToUpload, ...truncatedFiles]);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    function removeFile(idToRemove: string) {
        if (props.readOnly) return;
        props.onFilesChange((props.fileIds || []).filter(id => id !== idToRemove));
    }
    
    function removeFileToUpload(index: number) {
        if (props.readOnly) return;
        props.onFilesToUploadChange(props.filesToUpload.filter((_, i) => i !== index));
    }

    const currentCount = props.fileIds ? props.fileIds.length : 0;
    const pendingCount = props.filesToUpload.length;
    const uploading = props.uploading || false;

    return (
        <div style={{ marginTop: '10px' }}>
            {errorMessage && (
                <Message negative>
                    <Message.Header>Ошибка</Message.Header>
                    {errorMessage.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                    ))}
                </Message>
            )}
            
            {!props.readOnly && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        content="Выбрать файлы"
                        disabled={uploading || (currentCount + pendingCount) >= MAX_FILE_COUNT}
                        icon="attach"
                        labelPosition="left"
                        size="small"
                    />

                    {(currentCount > 0 || pendingCount > 0) && (
                        <span>Файлы: {currentCount + pendingCount} / {MAX_FILE_COUNT}</span>
                    )}
                </div>
            )}

            {props.readOnly && (currentCount > 0 || pendingCount > 0) && (
                <div style={{ marginBottom: '10px' }}>
                    <span>Файлы: {currentCount + pendingCount} / {MAX_FILE_COUNT}</span>
                </div>
            )}
            
            <input
                ref={fileInputRef}
                type="file"
                multiple={true}
                name="file"
                accept={ACCEPT_STRING}
                onChange={onFileSelect}
                title=""
                style={{ display: 'none' }}
            />
            
            {(pendingCount > 0 || filesInfo.length > 0) && (
                <Segment style={{ marginTop: '10px' }}>
                    {props.filesToUpload.map((f, i) => (
                        <div key={`pending-${i}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', marginTop: '5px' }}>
                            <span><Icon name="file outline" /> {f.name}</span>
                            <div>
                                {!isObserver && (
                                    <Icon 
                                        name="download" 
                                        color="blue" 
                                        style={{ cursor: 'pointer', marginRight: props.readOnly ? '0' : '10px' }} 
                                        onClick={() => onDownloadLocalFile(f)} 
                                        title="Скачать файл"
                                    />
                                )}
                                {!props.readOnly && !isObserver && (
                                    <Icon 
                                        name="close" 
                                        color="grey" 
                                        style={{ cursor: 'pointer' }} 
                                        onClick={() => removeFileToUpload(i)} 
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                    {filesInfo.map((f, i) => (
                        <div key={`attached-${f.id}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', marginTop: '5px' }}>
                            <span><Icon name="file outline" /> {f.filename}</span>
                            <div>
                                {!isObserver && (
                                    <Icon 
                                        name="download" 
                                        color="blue" 
                                        style={{ cursor: 'pointer', marginRight: props.readOnly ? '0' : '10px' }} 
                                        onClick={() => onDownloadFile(f.id, f.filename)} 
                                        title="Скачать файл"
                                    />
                                )}
                                {!props.readOnly && !isObserver && (
                                    <Icon 
                                        name="close" 
                                        color="red" 
                                        style={{ cursor: 'pointer' }} 
                                        onClick={() => removeFile(f.id)} 
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </Segment>
            )}
        </div>
    );
};

export default EventFilesUpload;

